import React, { useCallback, useEffect, useState } from "react";

import API from "./api";

import Sidebar from "./components/Sidebar";
import DashboardCards from "./components/DashboardCards";
import PaymentTable from "./components/PaymentTable";
import TemplatePage from "./components/TemplatePage";
import DirectoryPage from "./components/DirectoryPage";
import UsersPage from "./components/UsersPage";
import ReportsPage from "./components/ReportsPage";
import ContractorsPage from "./components/ContractorsPage";
import LoginPage from "./components/LoginPage";
import CompaniesPage from "./components/CompaniesPage";

import "./index.css";

const defaultTemplateColumns = [
  { id: "branch", key: "branch", label: "Филиал", format: "text", required: false },
  { id: "contractor", key: "contractor", label: "Контрагент", format: "text", required: false },
  { id: "amount", key: "amount", label: "Сумма", format: "number", required: false },
  { id: "purpose", key: "purpose", label: "Назначение", format: "text", required: false },
  { id: "initiator", key: "initiator", label: "Инициатор", format: "text", required: false }
];

const roleLabels = {
  SYSTEM_ADMIN: "Владелец платформы",
  ADMIN: "Админ",
  FINANCIER: "Финансист",
  DIRECTOR: "Директор",
  ACCOUNTANT: "Бухгалтер"
};

const defaultPaymentActions = [
  { id: "created", label: "Создано", status: "CREATED", showInMenu: false },
  { id: "approved", label: "Одобрено", status: "APPROVED", showInMenu: false },
  { id: "paid", label: "Оплачено", status: "PAID", showInMenu: false },
  { id: "rejected", label: "Отказано", status: "REJECTED", showInMenu: false }
];

const REGISTER_PAGE = "Реестр платежей";
const defaultMenuRoles = ["ADMIN", "FINANCIER", "DIRECTOR", "ACCOUNTANT"];

const normalizeActionColumns = (columns = []) =>
  columns.map((column) => ({
    ...column,
    optionsText: column.optionsText ||
      (column.options || []).map((option) => option.label).join("\n"),
    options: (column.options || []).map((option) => ({
      ...option,
      value: option.value || option.label,
      showInMenu: Boolean(option.showInMenu),
      menuLabel: option.menuLabel || option.label,
      menuRoles: option.menuRoles || defaultMenuRoles
    }))
  }));

const mapUser = (user) => ({
  ...user,
  role_label: roleLabels[user.role]
});

function App() {
  const [payments, setPayments] = useState([]);
  const [activePage, setActivePage] = useState(REGISTER_PAGE);
  const [templateColumns, setTemplateColumns] = useState(defaultTemplateColumns);
  const [paymentActions, setPaymentActions] = useState(defaultPaymentActions);
  const [actionColumns, setActionColumns] = useState([]);
  const [directories, setDirectories] = useState([]);
  const [counterparties, setCounterparties] = useState([]);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [sessionChecked, setSessionChecked] = useState(false);
  const [loginError, setLoginError] = useState("");

  const [branch, setBranch] = useState("");
  const [contractor, setContractor] = useState("");
  const [amount, setAmount] = useState("");
  const [purpose, setPurpose] = useState("");
  const [initiator, setInitiator] = useState("");
  const [extraFields, setExtraFields] = useState({});

  const [statusFilter, setStatusFilter] = useState("");
  const [message, setMessage] = useState("");
  const [paymentToDelete, setPaymentToDelete] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);

  const loadUsers = useCallback(async () => {
    const res = await API.get("/session/users");
    const availableUsers = res.data.map(mapUser);

    setUsers(availableUsers);
  }, []);

  const loadCompanies = async () => {
    const res = await API.get("/companies");
    setCompanies(res.data);
  };

  const loadPayments = async () => {
    const res = await API.get("/payments");
    setPayments(res.data);
  };

  const loadSettings = async () => {
    const res = await API.get("/settings");

    if (res.data.template_columns) {
      setTemplateColumns(res.data.template_columns);
    }

    setPaymentActions(res.data.payment_actions || defaultPaymentActions);
    setActionColumns(normalizeActionColumns(res.data.action_columns || []));
    setDirectories(res.data.directories || []);
    setSettingsLoaded(true);
  };

  const loadCounterparties = useCallback(async () => {
    if (!["ADMIN", "FINANCIER", "ACCOUNTANT"].includes(currentUser?.role)) {
      setCounterparties([]);
      return;
    }

    const res = await API.get("/counterparties");
    setCounterparties(res.data);
  }, [currentUser]);

  const saveSettings = async (
    nextTemplateColumns,
    nextDirectories,
    nextPaymentActions,
    nextActionColumns
  ) => {
    await API.put("/settings", {
      template_columns: nextTemplateColumns,
      directories: nextDirectories,
      payment_actions: nextPaymentActions,
      action_columns: nextActionColumns
    });
  };

  useEffect(() => {
    const loadSession = async () => {
      const token = localStorage.getItem("authToken");

      if (!token) {
        setSessionChecked(true);
        return;
      }

      try {
        const res = await API.get("/session/me");
        setCurrentUser(mapUser(res.data));
      } catch (error) {
        localStorage.removeItem("authToken");
        setCurrentUser(null);
      } finally {
        setSessionChecked(true);
      }
    };

    loadSession();
  }, []);

  useEffect(() => {
    if (!currentUser) {
      return;
    }

    setSettingsLoaded(false);

    if (currentUser.role === "SYSTEM_ADMIN") {
      setActivePage("Компании");
      loadCompanies();
      loadUsers();
      return;
    }

    loadPayments();
    loadSettings();
    loadCounterparties();
    loadUsers();

    if (currentUser.role !== "ADMIN") {
      setActivePage(REGISTER_PAGE);
    }
  }, [currentUser, loadCounterparties, loadUsers]);

  useEffect(() => {
    if (!settingsLoaded || currentUser?.role !== "ADMIN") {
      return;
    }

    saveSettings(templateColumns, directories, paymentActions, actionColumns);
  }, [templateColumns, directories, paymentActions, actionColumns, settingsLoaded, currentUser]);

  const login = async (username, password) => {
    try {
      const res = await API.post("/login", { username, password });
      const user = mapUser(res.data.user);

      localStorage.setItem("authToken", res.data.access_token);
      setLoginError("");
      setCurrentUser(user);
      setActivePage(user.role === "SYSTEM_ADMIN" ? "Компании" : REGISTER_PAGE);
    } catch (error) {
      setLoginError(error.response?.data?.detail || "Не удалось войти");
    }
  };

  const logout = () => {
    localStorage.removeItem("authToken");
    setCurrentUser(null);
    setActivePage(REGISTER_PAGE);
    setPayments([]);
    setUsers([]);
    setCompanies([]);
    setCounterparties([]);
  };

  const addUser = async (name, role, username, password) => {
    await API.post("/users", { name, role, username, password });
    await loadUsers();
  };

  const updateUserRole = async (userId, role, username, password) => {
    await API.put(`/users/${userId}/role`, { role });
    if (username !== undefined) {
      await API.put(`/users/${userId}/credentials`, { username, password: password || null });
    }
    await loadUsers();
  };

  const deleteUser = async (userId) => {
    await API.delete(`/users/${userId}`);
    await loadUsers();
    setUserToDelete(null);
  };

  const addCompany = async (payload) => {
    await API.post("/companies", payload);
    await loadCompanies();
  };

  const deleteCompany = async (companyId) => {
    await API.delete(`/companies/${companyId}`);
    await loadCompanies();
  };

  const addCounterparty = async (name) => {
    await API.post("/counterparties", { name });
    await loadCounterparties();
  };

  const getFieldValue = (column) => {
    if (column.key === "branch") return branch;
    if (column.key === "contractor") return contractor;
    if (column.key === "amount") return amount;
    if (column.key === "purpose") return purpose;
    if (column.key === "initiator") return initiator;

    return extraFields[column.id] || "";
  };

  const setFieldValue = (column, value) => {
    if (column.key === "branch") setBranch(value);
    else if (column.key === "contractor") setContractor(value);
    else if (column.key === "amount") setAmount(value);
    else if (column.key === "purpose") setPurpose(value);
    else if (column.key === "initiator") setInitiator(value);
    else setExtraFields((current) => ({
      ...current,
      [column.id]: value
    }));
  };

  const clearPaymentForm = () => {
    setBranch("");
    setContractor("");
    setAmount("");
    setPurpose("");
    setInitiator("");
    setExtraFields({});
  };

  const createPayment = async () => {
    const missingColumns = templateColumns
      .filter((column) => column.required && String(getFieldValue(column)).trim() === "")
      .map((column) => column.label);

    if (missingColumns.length > 0) {
      setMessage(`Не заполнены обязательные столбцы: ${missingColumns.join(", ")}`);
      return;
    }

    const paymentAmount = Number(amount);

    if (templateColumns.some((column) => column.key === "amount") &&
      amount.trim() !== "" &&
      Number.isNaN(paymentAmount)) {
      setMessage("Прошу ввести цифровое значение в поле Сумма");
      return;
    }

    const invalidNumberColumn = templateColumns.find((column) =>
      column.format === "number" &&
      column.key !== "amount" &&
      String(getFieldValue(column)).trim() !== "" &&
      Number.isNaN(Number(getFieldValue(column)))
    );

    if (invalidNumberColumn) {
      setMessage(`Прошу ввести цифровое значение в поле ${invalidNumberColumn.label}`);
      return;
    }

    const activeExtraFields = templateColumns
      .filter((column) => !column.key)
      .reduce((fields, column) => ({
        ...fields,
        [column.id]: extraFields[column.id] || ""
      }), {});

    await API.post("/payments", {
      branch,
      contractor,
      amount: Number.isNaN(paymentAmount) ? 0 : paymentAmount,
      purpose,
      initiator,
      extra_fields: activeExtraFields
    });

    clearPaymentForm();
    loadPayments();
    loadCounterparties();
  };

  const updatePaymentStatus = async (id, status) => {
    await API.put(`/payments/${id}/status`, { status });
    loadPayments();
  };

  const updatePaymentField = async (id, fieldId, value) => {
    await API.put(`/payments/${id}/field`, { field_id: fieldId, value });
    loadPayments();
  };

  const deletePayment = async (id) => {
    await API.delete(`/payments/${id}`);
    loadPayments();
    setPaymentToDelete(null);
  };

  const uploadExcel = async (e) => {
    const file = e.target.files[0];

    if (!file) {
      return;
    }

    const formData = new FormData();

    formData.append("file", file);

    try {
      const res = await API.post("/upload-excel", formData);

      await loadPayments();
      await loadCounterparties();
      setMessage(`Добавлено платежей из Excel: ${res.data.added_count}`);
    } catch (error) {
      setMessage(error.response?.data?.detail || "Не удалось загрузить Excel файл");
    } finally {
      e.target.value = "";
    }
  };

  const downloadTemplate = async () => {
    try {
      const res = await API.get("/payment-template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");

      link.href = url;
      link.setAttribute("download", "Шаблон_реестра_платежей.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setMessage(error.response?.data?.detail || "Не удалось скачать шаблон Excel");
    }
  };

  const actionMenuItems = actionColumns.flatMap((column) =>
    column.options
      .filter((option) =>
        option.showInMenu &&
        (option.menuRoles || defaultMenuRoles).includes(currentUser?.role)
      )
      .map((option) => ({
        id: `${column.id}:${option.value}`,
        label: option.menuLabel || option.label,
        columnId: column.id,
        value: option.value
      }))
  );
  const activeActionPage = actionMenuItems.find((item) => item.label === activePage);
  const selectedRegisterAction = actionMenuItems.find((item) => item.id === statusFilter);
  const isPaymentPage = activePage === REGISTER_PAGE || Boolean(activeActionPage);
  const filteredPayments = payments.filter((p) =>
    activeActionPage
      ? (p.extra_fields?.[activeActionPage.columnId] || "") === activeActionPage.value
      : selectedRegisterAction
        ? (p.extra_fields?.[selectedRegisterAction.columnId] || "") === selectedRegisterAction.value
        : true
  );
  const isSystemAdmin = currentUser?.role === "SYSTEM_ADMIN";
  const isAdmin = currentUser?.role === "ADMIN";
  const canViewReports = ["ADMIN", "FINANCIER", "DIRECTOR"].includes(currentUser?.role);
  const canViewCounterparties = ["ADMIN", "FINANCIER", "ACCOUNTANT"].includes(currentUser?.role);
  const canAddCounterparties = ["ADMIN", "FINANCIER"].includes(currentUser?.role);
  const canCreatePayment = ["ADMIN", "FINANCIER", "ACCOUNTANT"].includes(currentUser?.role);
  const canManageActions = ["ADMIN", "DIRECTOR"].includes(currentUser?.role);
  const canDeletePayment = ["ADMIN", "FINANCIER", "DIRECTOR"].includes(currentUser?.role);

  const addTemplateColumn = () => {
    const id = `extra_${Date.now()}`;

    setTemplateColumns((current) => [
      ...current,
      {
        id,
        label: "Новый столбец",
        format: "text",
        required: false
      }
    ]);
  };

  const updateTemplateColumn = (id, field, value) => {
    setTemplateColumns((current) =>
      current.map((column) =>
        column.id === id ? { ...column, [field]: value } : column
      )
    );
  };

  const removeTemplateColumn = (id) => {
    setTemplateColumns((current) =>
      current.filter((column) => column.id !== id)
    );
  };

  const clearTemplate = () => {
    setTemplateColumns([]);
    clearPaymentForm();
  };

  const moveTemplateColumn = (id, direction) => {
    setTemplateColumns((current) => {
      const index = current.findIndex((column) => column.id === id);
      const targetIndex = index + direction;

      if (index < 0 || targetIndex < 0 || targetIndex >= current.length) {
        return current;
      }

      const next = [...current];
      const [column] = next.splice(index, 1);
      next.splice(targetIndex, 0, column);

      return next;
    });
  };

  const launchTemplate = () => {
    clearPaymentForm();
    setActivePage(REGISTER_PAGE);
  };

  const addPaymentAction = () => {
    const id = `action_${Date.now()}`;

    setPaymentActions((current) => [
      ...current,
      {
        id,
        label: "Новое действие",
        status: `ACTION_${Date.now()}`,
        showInMenu: true
      }
    ]);
  };

  const updatePaymentAction = (id, field, value) => {
    setPaymentActions((current) =>
      current.map((action) => {
        if (action.id !== id) {
          return action;
        }

        if (field === "label") {
          return {
            ...action,
            label: value,
            status: action.status?.startsWith("ACTION_")
              ? value.trim() || action.status
              : action.status
          };
        }

        return { ...action, [field]: value };
      })
    );
  };

  const removePaymentAction = (id) => {
    setPaymentActions((current) =>
      current.filter((action) => action.id !== id)
    );
  };

  const addActionColumn = () => {
    const id = `action_column_${Date.now()}`;

    setActionColumns((current) => [
      ...current,
      {
        id,
        label: "Новый столбец",
        optionsText: "Вариант 1\nВариант 2",
        options: [
          {
            id: `${id}_option_1`,
            label: "Вариант 1",
            value: "Вариант 1",
            showInMenu: false,
            menuLabel: "Вариант 1",
            menuRoles: defaultMenuRoles
          },
          {
            id: `${id}_option_2`,
            label: "Вариант 2",
            value: "Вариант 2",
            showInMenu: false,
            menuLabel: "Вариант 2",
            menuRoles: defaultMenuRoles
          }
        ]
      }
    ]);
  };

  const normalizeActionOptions = (columnId, optionsText, currentOptions = []) => {
    return optionsText
      .split("\n")
      .map((label) => label.trim())
      .filter(Boolean)
      .map((label, index) => {
        const existing = currentOptions.find((option) => option.label === label);

        return {
          id: existing?.id || `${columnId}_option_${index}_${Date.now()}`,
          label,
          value: label,
          showInMenu: existing?.showInMenu || false,
          menuLabel: existing?.menuLabel || label,
          menuRoles: existing?.menuRoles || defaultMenuRoles
        };
      });
  };

  const updateActionColumn = (id, field, value) => {
    setActionColumns((current) =>
      current.map((column) => {
        if (column.id !== id) {
          return column;
        }

        if (field === "optionsText") {
          return {
            ...column,
            optionsText: value,
            options: normalizeActionOptions(column.id, value, column.options)
          };
        }

        return { ...column, [field]: value };
      })
    );
  };

  const updateActionColumnOption = (columnId, optionId, field, value) => {
    setActionColumns((current) =>
      current.map((column) =>
        column.id === columnId
          ? {
              ...column,
              options: column.options.map((option) =>
                option.id === optionId ? { ...option, [field]: value } : option
              )
            }
          : column
      )
    );
  };

  const removeActionColumn = (id) => {
    setActionColumns((current) =>
      current.filter((column) => column.id !== id)
    );
  };

  const addDirectory = () => {
    setDirectories((current) => [
      ...current,
      {
        id: `directory_${Date.now()}`,
        name: "Новый справочник",
        values: "",
        saved: false,
        savedName: "",
        savedValues: ""
      }
    ]);
  };

  const updateDirectory = (id, field, value) => {
    setDirectories((current) =>
      current.map((directory) =>
        directory.id === id ? { ...directory, [field]: value } : directory
      )
    );
  };

  const saveDirectory = (id) => {
    setDirectories((current) =>
      current.map((directory) =>
        directory.id === id
          ? {
              ...directory,
              saved: true,
              savedName: directory.name,
              savedValues: directory.values
            }
          : directory
      )
    );
  };

  const removeDirectory = (id) => {
    setDirectories((current) =>
      current.filter((directory) => directory.id !== id)
    );

    setTemplateColumns((current) =>
      current.map((column) =>
        column.format === `directory:${id}`
          ? { ...column, format: "text" }
          : column
      )
    );
  };

  const getDirectoryByFormat = (format) => {
    if (!format.startsWith("directory:")) {
      return null;
    }

    const directoryId = format.replace("directory:", "");

    return directories.find((directory) =>
      directory.id === directoryId && directory.saved
    ) || null;
  };

  const getDirectoryValues = (directory) => {
    if (!directory) {
      return [];
    }

    return directory.savedValues
      .split("\n")
      .map((value) => value.trim())
      .filter(Boolean);
  };

  const renderTemplateInput = (column) => {
    const directory = getDirectoryByFormat(column.format);

    if (column.key === "contractor") {
      return (
        <select
          key={column.id}
          value={contractor}
          onChange={(e) => setContractor(e.target.value)}
        >
          <option value="">
            {column.required ? `${column.label} *` : column.label}
          </option>

          {counterparties.map((counterparty) => (
            <option key={counterparty} value={counterparty}>
              {counterparty}
            </option>
          ))}
        </select>
      );
    }

    if (directory) {
      return (
        <select
          key={column.id}
          value={getFieldValue(column)}
          onChange={(e) => setFieldValue(column, e.target.value)}
        >
          <option value="">
            {column.required ? `${column.label} *` : column.label}
          </option>

          {getDirectoryValues(directory).map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>
      );
    }

    return (
      <input
        key={column.id}
        placeholder={column.required ? `${column.label} *` : column.label}
        value={getFieldValue(column)}
        onChange={(e) => setFieldValue(column, e.target.value)}
        type={column.format === "date" ? "date" : "text"}
        inputMode={column.format === "number" ? "decimal" : undefined}
      />
    );
  };

  if (!sessionChecked) {
    return null;
  }

  if (!currentUser) {
    return <LoginPage login={login} error={loginError} />;
  }

  return (
    <div className="app">
      <Sidebar
        activePage={activePage}
        setActivePage={setActivePage}
        currentUser={currentUser}
        paymentActions={paymentActions}
        actionMenuItems={actionMenuItems}
        logout={logout}
      />

      <div className="content">
        {isSystemAdmin && activePage === "Компании" && (
          <CompaniesPage
            companies={companies}
            addCompany={addCompany}
            deleteCompany={deleteCompany}
          />
        )}

        {!isSystemAdmin && isPaymentPage && (
          <>
            <h1>{activePage}</h1>

            {activePage === REGISTER_PAGE && (
              <DashboardCards
                payments={payments}
                actionColumns={actionColumns}
                paymentActions={paymentActions}
              />
            )}

            {activePage === REGISTER_PAGE && canCreatePayment && (
              <div className="filters">
                {templateColumns.map(renderTemplateInput)}

                <button onClick={createPayment}>
                  Создать платеж
                </button>
              </div>
            )}

            {activePage === REGISTER_PAGE && (
              <div className="filters register-tools">
                {actionMenuItems.length > 0 && (
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="">Все значения</option>
                    {actionMenuItems.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                )}

                {canCreatePayment && (
                  <>
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={uploadExcel}
                    />

                    <button
                      className="download-template-btn"
                      onClick={downloadTemplate}
                    >
                      Скачать шаблон Excel
                    </button>
                  </>
                )}
              </div>
            )}

            <PaymentTable
              payments={filteredPayments}
              templateColumns={templateColumns}
              paymentActions={paymentActions}
              actionColumns={actionColumns}
              updatePaymentStatus={updatePaymentStatus}
              updatePaymentField={updatePaymentField}
              deletePayment={setPaymentToDelete}
              showActions={canManageActions}
              canDelete={canDeletePayment}
            />
          </>
        )}

        {isAdmin && activePage === "Шаблон" && (
          <TemplatePage
            templateColumns={templateColumns}
            directories={directories.filter((directory) => directory.saved)}
            paymentActions={paymentActions}
            actionColumns={actionColumns}
            updateTemplateColumn={updateTemplateColumn}
            addTemplateColumn={addTemplateColumn}
            clearTemplate={clearTemplate}
            removeTemplateColumn={removeTemplateColumn}
            moveTemplateColumn={moveTemplateColumn}
            launchTemplate={launchTemplate}
            addPaymentAction={addPaymentAction}
            updatePaymentAction={updatePaymentAction}
            removePaymentAction={removePaymentAction}
            addActionColumn={addActionColumn}
            updateActionColumn={updateActionColumn}
            updateActionColumnOption={updateActionColumnOption}
            removeActionColumn={removeActionColumn}
          />
        )}

        {isAdmin && activePage === "Справочник" && (
          <DirectoryPage
            directories={directories}
            addDirectory={addDirectory}
            updateDirectory={updateDirectory}
            saveDirectory={saveDirectory}
            removeDirectory={removeDirectory}
          />
        )}

        {isAdmin && activePage === "Пользователи" && (
          <UsersPage
            users={users}
            currentUser={currentUser}
            addUser={addUser}
            updateUserRole={updateUserRole}
            deleteUser={setUserToDelete}
          />
        )}

        {canViewCounterparties && activePage === "Контрагенты" && (
          <ContractorsPage
            counterparties={counterparties}
            payments={payments}
            addCounterparty={addCounterparty}
            canAdd={canAddCounterparties}
          />
        )}

        {canViewReports && activePage === "Отчеты" && (
          <ReportsPage
            payments={payments}
            templateColumns={templateColumns}
            paymentActions={paymentActions}
            actionColumns={actionColumns}
          />
        )}

        {!isPaymentPage &&
          activePage !== "Компании" &&
          activePage !== "Шаблон" &&
          activePage !== "Справочник" &&
          activePage !== "Пользователи" &&
          activePage !== "Контрагенты" &&
          activePage !== "Отчеты" && (
          <div>
            <h1>{activePage}</h1>
            <p>Раздел пока не настроен.</p>
          </div>
        )}

        {message && (
          <div className="msgbox-overlay">
            <div className="msgbox">
              <p>{message}</p>
              <button onClick={() => setMessage("")}>
                OK
              </button>
            </div>
          </div>
        )}

        {paymentToDelete && (
          <div className="msgbox-overlay">
            <div className="msgbox">
              <p>Вы уверены?</p>

              <div className="msgbox-actions">
                <button onClick={() => deletePayment(paymentToDelete)}>
                  Да
                </button>

                <button
                  className="secondary-btn"
                  onClick={() => setPaymentToDelete(null)}
                >
                  Нет
                </button>
              </div>
            </div>
          </div>
        )}

        {userToDelete && (
          <div className="msgbox-overlay">
            <div className="msgbox">
              <p>Вы уверены?</p>

              <div className="msgbox-actions">
                <button onClick={() => deleteUser(userToDelete)}>
                  Да
                </button>

                <button
                  className="secondary-btn"
                  onClick={() => setUserToDelete(null)}
                >
                  Нет
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
